import React from "react";
import ModalForm from "@ui/Modal/Form";
import { useSetups, type Setup } from "../../../../../api/useSetups";
import type { AlgorithmPreset } from "../../../../../api/useAlgorithmPresets";
import { useAlgorithmPresets } from "../../../../../api/useAlgorithmPresets";
import Select from "@ui/Select";
import t from "../../../../../i18n";
import useInput from "@ui/lib/hooks/useInput";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { H4 } from "@ui/Text";
import { colors } from "@ui/Theme";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import InputV2 from "@ui/Input/InputV2";
import Checkbox from "@ui/Checkbox";
import type { Index } from "diagnostics-lib/iterable";
import { indexBy } from "diagnostics-lib/iterable";
import { usePost } from "../../../../../api/useCall";
import { useImpactedAnalyses } from "../useAnalysisImpacted";
import useActiveAnalysesStream from "../../../../../api/useActiveAnalysesStream";

interface P {
  projectId: string;
  setup: Setup;
  onRequestClose: () => void;
  okButtonName: string;
}

const StyledModalForm = styled(ModalForm)`
  row-gap: 32px;
`;

const SectionParameters = styled.div`
  display: flex;
  flex-direction: column;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &:not(:last-child) {
      padding-bottom: 12px;
    }
  }
`;

const H4Css = css`
  margin-bottom: 16px;
  color: ${colors.carbon700};
`;

type StateType = {
  [key: string]: {
    [key: string]: {
      value: string;
      dirty: boolean;
      error: string;
    };
  };
};
type UpdateAction = {
  type: "update";
  presetKey: string;
  paramKey: string;
  value: string;
  dirty: boolean;
  error: string;
};
type ResetAction = {
  type: "reset";
  initialFormValue: StateType;
};
const reducer = (formValue: StateType, action: UpdateAction | ResetAction) => {
  switch (action.type) {
    case "reset":
      return action.initialFormValue;
    case "update": {
      const preset = formValue[action.presetKey];
      const param = preset?.[action.paramKey];
      if (!preset || !param) return formValue;
      return {
        ...formValue,
        [action.presetKey]: {
          ...preset,
          [action.paramKey]: {
            ...param,
            value: action.value,
            dirty: action.dirty,
            error: action.error,
          },
        },
      };
    }
    default:
      throw new Error(action satisfies never);
  }
};

const analysisStatus = new Set(["DIAGNOSIS", "DIAGNOSIS_ERROR"] as const);

const createInitialFormValue = (
  algorithmPresets: AlgorithmPreset[],
  setupPresetKey: string | null,
  indexSetupParameterByKey: Index<
    {
      key: string;
      value: number | boolean;
    },
    string
  >,
) => {
  const initialFormValue: StateType = {};
  for (const preset of algorithmPresets) {
    const objectParams: {
      [key: string]: {
        value: string;
        dirty: boolean;
        error: string;
      };
    } = {};
    for (const param of preset.parameters) {
      const value =
        setupPresetKey === preset.key ? indexSetupParameterByKey.get(param.key)?.value : null;
      objectParams[param.key] = {
        value: value?.toString() ?? param.defaultValue.toString(),
        dirty: false,
        error: "",
      };
    }
    initialFormValue[preset.key] = objectParams;
  }
  return initialFormValue;
};

const UpdateAlgorithmModalForm = ({ projectId, setup, onRequestClose, okButtonName }: P) => {
  const algorithmPresets = useAlgorithmPresets();
  const updatePreset = usePost("/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/preset");
  const setupId = setup.id;

  /**
   * Setup
   */
  const { diagnosisPreset, diagnosisParameters } = setup;
  const indexSetupParametersBykey = indexBy(diagnosisParameters, "key");

  const presetOptions = React.useMemo(
    () =>
      algorithmPresets
        .map(version => ({
          value: version.key,
          label: version.translateKey,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [algorithmPresets],
  );

  const setupPresetKey = presetOptions.some(p => p.value === diagnosisPreset)
    ? diagnosisPreset
    : null;

  /**
   * Input Version
   */
  const {
    value: formValuePresetKey,
    onChange: onChangeFormValuePresetKey,
    setValue: setFormValuePresetKey,
    dirty: presetDirty,
  } = useInput(setupPresetKey ?? "");

  /**
   * Input Parameters
   */
  const initialFormValue: StateType = createInitialFormValue(
    algorithmPresets,
    setupPresetKey,
    indexSetupParametersBykey,
  );
  const [formValue, dispatch] = React.useReducer(reducer, initialFormValue);
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalysisCount = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  }).length;

  /**
   * --> Merge Presets and Setup Parameters
   */
  const parameterPresets = React.useMemo(
    () =>
      algorithmPresets.map(presetData => {
        const setupParameters = setupPresetKey === presetData.key ? setup.diagnosisParameters : [];
        return {
          ...presetData,
          parameters: presetData.parameters.map(param => ({
            ...param,
            defaultValue: setupParameters
              ? (indexSetupParametersBykey.get(param.key)?.value.toString() ??
                param.defaultValue.toString())
              : param.defaultValue.toString(),
            defaultValueSource: param.defaultValue.toString(),
          })),
        };
      }),
    [algorithmPresets, setupPresetKey, setup.diagnosisParameters, indexSetupParametersBykey],
  );
  const parametersSelected = React.useMemo(
    () =>
      parameterPresets
        .find(f => f.key === formValuePresetKey)
        ?.parameters.sort((a, b) => a.translateKey.localeCompare(b.translateKey)) ?? [],
    [parameterPresets, formValuePresetKey],
  );

  /**
   * Handlers
   */
  const handleSubmit = React.useCallback(async () => {
    const updatePresetData = {
      presetKey: formValuePresetKey,
      parameters: parametersSelected.map(param => {
        const value =
          param.type === "bool"
            ? Boolean(formValue[formValuePresetKey]?.[param.key]?.value)
            : param.type === "int" || param.type === "float"
              ? Number(formValue[formValuePresetKey]?.[param.key]?.value)
              : undefined;
        if (value === undefined) throw new Error("Parameter value is undefined");
        return { key: param.key, value };
      }),
    };

    await updatePreset({
      routeParams: { projectId, setupId: setup.id },
      body: updatePresetData,
    });

    return true;
  }, [projectId, setup.id, formValuePresetKey, parametersSelected, formValue, updatePreset]);
  const handleInput = React.useCallback(
    (
      inputPresetKey: string,
      inputPresetParamKey: string,
      inputPresetParamNumber: string,
      defaultParamValue: string,
      min: number | null,
      max: number | null,
    ) => {
      const dirty = inputPresetParamNumber !== defaultParamValue;
      const errorMin =
        min !== null && Number(inputPresetParamNumber) < min
          ? t("setup.diagnosis.modal_form.algorithm.input_number.error.min", min)
          : "";
      const errorMax =
        max !== null && Number(inputPresetParamNumber) > max
          ? t("setup.diagnosis.modal_form.algorithm.input_number.error.max", max)
          : "";
      const error = errorMin || errorMax;
      dispatch({
        type: "update",
        value: inputPresetParamNumber,
        presetKey: inputPresetKey,
        paramKey: inputPresetParamKey,
        dirty,
        error,
      });
    },
    [],
  );
  const handlerCheckbox = React.useCallback(
    (
      inputPresetkey: string,
      inputPresetParamKey: string,
      defaultParamValue: string,
      value: string,
    ) => {
      dispatch({
        type: "update",
        value,
        presetKey: inputPresetkey,
        paramKey: inputPresetParamKey,
        dirty: value !== defaultParamValue,
        error: "",
      });
    },
    [],
  );

  /**
   * Check Submit
   */
  const dirty = React.useMemo(
    () =>
      presetDirty || parametersSelected.some(d => formValue[formValuePresetKey]?.[d.key]?.dirty),
    [presetDirty, parametersSelected, formValuePresetKey, formValue],
  );
  const hasError = React.useMemo(
    () =>
      !formValuePresetKey ||
      parametersSelected.some(p => formValue[formValuePresetKey]?.[p.key]?.error),
    [parametersSelected, formValuePresetKey, formValue],
  );

  React.useEffect(() => {
    dispatch({ type: "reset", initialFormValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithmPresets]);

  return (
    <StyledModalForm
      onSubmit={dirty && !hasError ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={okButtonName}
    >
      {/* PRESETS */}
      <Select
        label={t("setup.diagnosis.modal_form.algorithm.select.label")}
        onChange={value => onChangeFormValuePresetKey(value)}
        value={formValuePresetKey}
        options={presetOptions}
        prompt="version"
        clearable={false}
        revertable={presetDirty}
        revert={() => setFormValuePresetKey(setupPresetKey ?? "")}
      />

      {/* PARAMETERS */}
      {parametersSelected.length > 0 ? (
        <SectionParameters>
          <H4 className={H4Css}>{t("setup.diagnosis.modal_form.section.modifiable_parameters")}</H4>
          {parametersSelected.map(parameterSelected => {
            const initialValueParameter = parameterSelected.defaultValue;
            const formValueParameter = formValue[formValuePresetKey]?.[parameterSelected.key];
            if (formValueParameter === undefined) return null;

            /**
             * Additionnal message
             */
            if (parameterSelected.type === "bool") {
              return (
                <div key={`checkbox-${formValuePresetKey + "-" + parameterSelected.key}`}>
                  <Checkbox
                    onChange={e =>
                      handlerCheckbox(
                        formValuePresetKey,
                        parameterSelected.key,
                        initialValueParameter,
                        e.target.checked.toString(),
                      )
                    }
                    checked={Boolean(formValueParameter.value)}
                  >
                    {parameterSelected.translateKey}
                  </Checkbox>
                  {parameterSelected.defaultValueSource !== formValueParameter.value && (
                    <AdditionnalMessage
                      key={`message-${formValuePresetKey + "-" + parameterSelected.key}`}
                      status="INFO"
                      text={t(
                        "setup.diagnosis.modal_form.algorithm.input_boolean.value_diff_default_value",
                        Boolean(parameterSelected.defaultValueSource),
                      )}
                    />
                  )}
                </div>
              );
            } else if (parameterSelected.type === "int" || parameterSelected.type === "float") {
              return (
                <div key={`input-${formValuePresetKey + "-" + parameterSelected.key}`}>
                  <InputV2
                    label={parameterSelected.translateKey}
                    defaultValue={initialValueParameter}
                    value={formValueParameter.value}
                    dirty={formValueParameter.dirty}
                    error={formValueParameter.error}
                    type={parameterSelected.type === "int" ? "number" : "float"}
                    step={1}
                    min={parameterSelected.minValue ?? undefined}
                    max={parameterSelected.maxValue ?? undefined}
                    onChange={e =>
                      handleInput(
                        formValuePresetKey,
                        parameterSelected.key,
                        e.target.value,
                        initialValueParameter,
                        parameterSelected.minValue,
                        parameterSelected.maxValue,
                      )
                    }
                  />
                  {parameterSelected.defaultValueSource !== formValueParameter.value &&
                    !formValueParameter.error && (
                      <AdditionnalMessage
                        key={`message-${formValuePresetKey + "-" + parameterSelected.key}`}
                        status="INFO"
                        text={t(
                          "setup.diagnosis.modal_form.algorithm.input_number.value_diff_default_value",
                          parameterSelected.defaultValueSource,
                        )}
                      />
                    )}
                </div>
              );
            } else return null;
          })}
        </SectionParameters>
      ) : null}

      {/* MESSAGES */}
      {dirty && (
        <AdditionnalMessage
          status="WARNING"
          text={t(
            "setup.diagnosis.modal_form.algorithm.message.update_algorithm",
            impactedAnalysisCount,
          )}
        />
      )}
    </StyledModalForm>
  );
};

export default React.memo(UpdateAlgorithmModalForm);
